import { Box, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { SEO } from "../components/SEO.js";

const sectionHeading = { fontSize: "1.3rem", color: "text.primary", mt: 5, mb: 2 } as const;
const subHeading = { fontSize: "1rem", color: "text.primary", mt: 3, mb: 1, fontWeight: 700 } as const;
const bodyBlock = {
  color: "text.secondary",
  "& p": { mb: 2, lineHeight: 1.8 },
  "& a": { color: "primary.main", textDecoration: "none" },
  "& a:hover": { textDecoration: "underline" },
} as const;

// ────────────────────────────────────────────────────────────────
// Privacy
// ────────────────────────────────────────────────────────────────
export function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy policy for Corian Park. How we collect, use, store, and protect your personal information under Australian privacy law."
        canonical="https://corianpark.au/privacy"
      />
      <Box sx={{ maxWidth: 820 }}>
        <Typography variant="h1" sx={{ fontSize: "clamp(2rem, 5vw, 3rem)", mb: 1 }}>
          PRIVACY POLICY
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: 24 April 2026
        </Typography>
        <Box sx={bodyBlock}>
          <Typography variant="body1">
            Corian Park (<strong>"Corian Park"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, <strong>"our"</strong>) is operated by Marwood X Pty Ltd (ACN 695 551 538). We respect your privacy and are committed to protecting your personal information in accordance with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth). This policy explains how we collect, use, disclose, and safeguard your information when you use our website (corianpark.au), our quote and referral services, and our curated content platform.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>1. INFORMATION WE COLLECT</Typography>

          <Typography variant="h4" sx={subHeading}>1.1 Information you provide</Typography>
          <Typography variant="body1">
            <strong>Quote requests:</strong> your name, email, phone number, postcode, project type, scale hint, and project description when you submit a quote through our referral form.
          </Typography>
          <Typography variant="body1">
            <strong>Contact form:</strong> your name, email, and message when you contact us.
          </Typography>
          <Typography variant="body1">
            <strong>Account access:</strong> your email address (and name) when you sign in via Google OAuth or magic link. Public users sign in to use the Loader Bucket and save content; the Corian Park team uses the same accounts for content review.
          </Typography>

          <Typography variant="h4" sx={subHeading}>1.2 Information collected automatically</Typography>
          <Typography variant="body1">
            With your consent, we use Google Analytics (GA4) to collect usage data such as pages visited, time on site, device type, and approximate location. Analytics cookies are only set after you provide consent.
          </Typography>

          <Typography variant="h4" sx={subHeading}>1.3 Content curated from third-party sources</Typography>
          <Typography variant="body1">
            Our content feed is curated from public sources (YouTube, RSS feeds, Reddit) and links back to the original publishers. We do not collect personal information about the creators of that content beyond what is publicly published — channel name, video title, and public metadata.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>2. HOW WE USE YOUR INFORMATION</Typography>
          <Typography variant="body1">We use your information to:</Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.8 } }}>
            <li>Route your quote request to a suitable operator in our referral network</li>
            <li>Generate a ballpark cost estimate for your request (via Truk) to give the operator context</li>
            <li>Send you confirmation and follow-up about your quote or enquiry</li>
            <li>Authenticate your sign-in and store your saved items (Loader Bucket)</li>
            <li>Analyse website usage to improve user experience (with consent)</li>
            <li>Comply with legal obligations</li>
          </Box>

          <Typography variant="h3" sx={sectionHeading}>3. DISCLOSURE OF INFORMATION</Typography>
          <Typography variant="body1">
            We do not sell, rent, or trade your personal information. We may share your information with:
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.8 } }}>
            <li>
              <strong>Referral operators</strong> — the independent earthmoving operator(s) your quote is routed to receive your contact details and project description so they can reach out. Operators are not our employees or agents; they contract with you directly.
            </li>
            <li>
              <strong>Service providers</strong> — cloud hosting (Amazon Web Services, ap-southeast-2 Sydney region), transactional email (Amazon SES via api.marwood.au), AI services (Google Gemini for content scoring and Dirt Talk generation), analytics (Google Analytics). These providers process data on our behalf under contractual obligations.
            </li>
            <li>
              <strong>Related Marwood services</strong> — we pass quote details to Truk (truk.au) to compute a ballpark estimate and to Tractrs (tractrs.com) to look up equipment references. Both are operated by Marwood X Pty Ltd under the same privacy standards.
            </li>
            <li>
              <strong>Legal requirements</strong> — where required by Australian law, court order, or government authority.
            </li>
          </Box>

          <Typography variant="h3" sx={sectionHeading}>4. REFERRAL NETWORK</Typography>
          <Typography variant="body1">
            When you submit a quote request, Corian Park acts as an introducer between you and an independent operator. Any agreement for earthworks is between you and the operator directly — we are not a party to that contract and do not perform the work ourselves. You can ask us to remove your details from the referral queue at any time by emailing{" "}
            <a href="mailto:hello@corianpark.au">hello@corianpark.au</a>.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>5. DATA STORAGE AND SECURITY</Typography>
          <Typography variant="body1">
            Your data is stored on Amazon Web Services (AWS) infrastructure in the ap-southeast-2 (Sydney) region. We implement industry-standard security measures including encryption in transit (TLS), encrypted storage, and access controls.
          </Typography>
          <Typography variant="body1">
            While we take reasonable steps to protect your information, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>6. DATA RETENTION AND DELETION</Typography>
          <Typography variant="body1">
            We retain your personal information only for as long as necessary to provide our services or as required by law:
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.8 } }}>
            <li>Quote requests — retained for 24 months after submission, unless you request earlier deletion</li>
            <li>Contact messages — retained for 12 months</li>
            <li>User accounts and saved items — retained while the account is active</li>
            <li>Analytics data — subject to Google Analytics retention settings (14 months)</li>
          </Box>
          <Typography variant="body1">
            You may request deletion of your personal information at any time by contacting us at{" "}
            <a href="mailto:hello@corianpark.au">hello@corianpark.au</a>. We will respond to deletion requests within 30 days and will delete or de-identify your data unless we are required by law to retain it.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>7. COOKIES</Typography>
          <Typography variant="body1">
            We use cookies only for analytics purposes (Google Analytics GA4), and only with your explicit consent. No analytics cookies are set until you accept. Essential cookies for session authentication are not tracking cookies and are required for sign-in to function.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>8. YOUR RIGHTS</Typography>
          <Typography variant="body1">Under the Australian Privacy Principles, you have the right to:</Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.8 } }}>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate or outdated information</li>
            <li>Request deletion of your personal information</li>
            <li>Withdraw consent for analytics cookies at any time</li>
            <li>Ask us to stop referring your quote to operators</li>
            <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) if you believe we have breached your privacy</li>
          </Box>

          <Typography variant="h3" sx={sectionHeading}>9. CHILDREN'S PRIVACY</Typography>
          <Typography variant="body1">
            Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child without parental consent, we will delete it promptly.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>10. CHANGES TO THIS POLICY</Typography>
          <Typography variant="body1">
            We may update this privacy policy from time to time. Material changes will be posted on this page with an updated "Last updated" date. Your continued use of our services after changes are posted constitutes acceptance of the revised policy.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>11. CONTACT</Typography>
          <Typography variant="body1">
            For privacy enquiries, data access requests, or complaints, contact us at:
          </Typography>
          <Box sx={{ mt: 1, pl: 2, borderLeft: "2px solid", borderColor: "divider", "& p": { mb: 0.5 } }}>
            <Typography variant="body1">Marwood X Pty Ltd (trading as Corian Park)</Typography>
            <Typography variant="body1">ACN 695 551 538</Typography>
            <Typography variant="body1">Bendigo, Victoria, Australia</Typography>
            <Typography variant="body1">
              Email: <a href="mailto:hello@corianpark.au">hello@corianpark.au</a>
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Terms
// ────────────────────────────────────────────────────────────────
export function Terms() {
  return (
    <>
      <SEO
        title="Terms of Service"
        description="Terms of service for Corian Park. Governing the use of our website, content platform, and operator referral service."
        canonical="https://corianpark.au/terms"
      />
      <Box sx={{ maxWidth: 820 }}>
        <Typography variant="h1" sx={{ fontSize: "clamp(2rem, 5vw, 3rem)", mb: 1 }}>
          TERMS OF SERVICE
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: 24 April 2026
        </Typography>
        <Box sx={bodyBlock}>
          <Typography variant="body1">
            These Terms of Service (<strong>"Terms"</strong>) govern your use of the website and services operated by Marwood X Pty Ltd (ACN 695 551 538) under the Corian Park brand (<strong>"Corian Park"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, <strong>"our"</strong>), including corianpark.au, our curated content feed, Dirt Talk and Rig of the Week features, and our operator referral service (collectively, the <strong>"Services"</strong>). By accessing or using our Services, you agree to be bound by these Terms. If you do not agree, do not use our Services.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>1. OUR SERVICES</Typography>
          <Typography variant="body1">Corian Park provides:</Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.8 } }}>
            <li>
              <strong>A curated content platform</strong> — Australian earthmoving, civil, and mining video and articles sourced from public third-party publishers and surfaced with editorial curation
            </li>
            <li>
              <strong>Dirt Talk and Rig of the Week</strong> — daily and weekly editorial content featuring machinery, operators, and industry stories
            </li>
            <li>
              <strong>Postcode landing pages ("Built near you")</strong> — location-based pages aggregating content and operators for Australian postcodes
            </li>
            <li>
              <strong>Equipment reference pages</strong> — machine specifications surfaced from Tractrs, our sibling machinery database
            </li>
            <li>
              <strong>An operator referral service</strong> — a form that routes your quote request to an independent earthmoving operator in our network
            </li>
          </Box>

          <Typography variant="h3" sx={sectionHeading}>2. ACCOUNTS</Typography>
          <Typography variant="body1">
            You can browse content, submit a quote, or contact us without an account. Optional sign-in (Google OAuth or magic-link email) unlocks the Loader Bucket and other personalised features. We do not use passwords — all authentication is via third-party OAuth or one-time email links. The Corian Park team uses the same authentication for content review and approval.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>3. CONTENT FROM THIRD-PARTY SOURCES</Typography>
          <Typography variant="body1">
            Our content feed includes videos, articles, and posts curated from public third-party sources (YouTube, RSS feeds, Reddit). All third-party content remains the property of its original publisher and is surfaced on Corian Park with attribution and a link back to the original source. We do not host the original video or article content ourselves and are not responsible for its accuracy, availability, or continued presence on the source platform.
          </Typography>
          <Typography variant="body1">
            If you are the owner of content that appears on Corian Park and would like it removed, email{" "}
            <a href="mailto:hello@corianpark.au">hello@corianpark.au</a> with a link to the item and we will action the request promptly.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>4. AI-GENERATED CONTENT</Typography>
          <Typography variant="body1">
            Dirt Talk cards, Rig of the Week rationales, and content summaries may be generated or assisted by AI models (Google Gemini). AI outputs may contain inaccuracies. Every AI-generated item is either reviewed by the Corian Park team before publication or clearly flagged as auto-published when the review window lapses. We make no warranty as to the accuracy or completeness of AI-generated content.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>5. QUOTE AND REFERRAL SERVICE</Typography>

          <Typography variant="h4" sx={subHeading}>5.1 How referrals work</Typography>
          <Typography variant="body1">
            When you submit a quote request, Corian Park acts as an introducer — we pass your request to an independent earthmoving operator in our referral network. Corian Park is not a party to any agreement between you and the operator. We do not perform the earthworks ourselves and are not responsible for the quality, timeliness, pricing, or safety of work performed by referred operators.
          </Typography>

          <Typography variant="h4" sx={subHeading}>5.2 Truk estimates</Typography>
          <Typography variant="body1">
            When available, we may attach a ballpark cost estimate (computed by Truk, our sibling pricing service) to your request to give the operator context. These estimates are indicative only and are not binding quotes. The final quote is set by the operator in consultation with you.
          </Typography>

          <Typography variant="h4" sx={subHeading}>5.3 Your responsibilities</Typography>
          <Typography variant="body1">
            You must provide accurate information in your quote request. You are responsible for all commercial, safety, and contractual arrangements entered into with any referred operator. You must satisfy yourself that the operator holds appropriate licences, insurances, and certifications for the work you engage them to perform.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>6. INTELLECTUAL PROPERTY</Typography>
          <Typography variant="body1">
            The Corian Park name, branding, layout, editorial copy (About, Dirt Talk phrasing, rationale text we write), and original graphics are the intellectual property of Marwood X Pty Ltd. You may not reproduce, distribute, or commercially exploit our original editorial content or branding without our written consent.
          </Typography>
          <Typography variant="body1">
            Third-party content surfaced on Corian Park remains the intellectual property of its original publisher and is used under the terms of the source platform's embedding and syndication mechanisms (e.g., YouTube embedded players, RSS syndication).
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>7. ACCEPTABLE USE</Typography>
          <Typography variant="body1">You agree not to:</Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1, lineHeight: 1.8 } }}>
            <li>Use our Services for any unlawful purpose or in violation of any applicable law</li>
            <li>Submit a quote request, contact message, or any other content that is defamatory, fraudulent, or misrepresents your identity or project</li>
            <li>Attempt to gain unauthorised access to our systems, the admin dashboard, or other users' data</li>
            <li>Use automated tools to scrape, crawl, or extract data from our Services beyond what is exposed in our public sitemap and RSS endpoints</li>
            <li>Interfere with or disrupt the integrity or performance of our Services</li>
            <li>Impersonate any person or entity, or misrepresent your affiliation with Corian Park, Marwood X, or any referred operator</li>
          </Box>

          <Typography variant="h3" sx={sectionHeading}>8. LIMITATION OF LIABILITY</Typography>
          <Typography variant="body1">
            To the maximum extent permitted by law, Corian Park's and Marwood X Pty Ltd's total liability to you for any claim arising from or related to these Terms or your use of the Services is limited to AUD $100 (one hundred dollars).
          </Typography>
          <Typography variant="body1">
            We are not liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, business opportunities, or goodwill, regardless of whether we were advised of the possibility of such damages. In particular, we are not liable for any loss arising from the acts, omissions, pricing, workmanship, safety record, or insurance status of any operator we refer you to — those relationships are between you and the operator directly.
          </Typography>
          <Typography variant="body1">
            Nothing in these Terms excludes or limits liability that cannot be excluded under Australian Consumer Law (Schedule 2 of the Competition and Consumer Act 2010 (Cth)).
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>9. INDEMNIFICATION</Typography>
          <Typography variant="body1">
            You agree to indemnify and hold harmless Corian Park, Marwood X Pty Ltd, its directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from: (a) your use of the Services; (b) any quote, contact message, or other content you submit through the Services; (c) your breach of these Terms; or (d) your violation of any third-party rights or applicable law.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>10. DISCLAIMER OF WARRANTIES</Typography>
          <Typography variant="body1">
            Our Services are provided "as is" and "as available" without warranties of any kind, whether express or implied. We do not guarantee that our Services will be uninterrupted, error-free, or secure. We do not guarantee that any quote request will be matched to an operator, or that a matched operator will accept or deliver the work.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>11. TERMINATION</Typography>
          <Typography variant="body1">
            We may suspend or terminate your access to the Services at any time, with or without cause, and with or without notice. You may stop using the Services at any time. Sections that by their nature should survive termination (including intellectual property, limitation of liability, indemnification, and governing law) will survive.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>12. GOVERNING LAW</Typography>
          <Typography variant="body1">
            These Terms are governed by and construed in accordance with the laws of the State of Victoria, Australia. You irrevocably submit to the exclusive jurisdiction of the courts of Victoria and the Federal Court of Australia sitting in Victoria for the resolution of any disputes arising from or relating to these Terms.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>13. CHANGES TO THESE TERMS</Typography>
          <Typography variant="body1">
            We may update these Terms from time to time. Material changes will be posted on this page with an updated "Last updated" date. Your continued use of the Services after changes are posted constitutes acceptance of the revised Terms.
          </Typography>

          <Typography variant="h3" sx={sectionHeading}>14. CONTACT</Typography>
          <Typography variant="body1">For questions about these Terms, contact us at:</Typography>
          <Box sx={{ mt: 1, pl: 2, borderLeft: "2px solid", borderColor: "divider", "& p": { mb: 0.5 } }}>
            <Typography variant="body1">Marwood X Pty Ltd (trading as Corian Park)</Typography>
            <Typography variant="body1">ACN 695 551 538</Typography>
            <Typography variant="body1">Bendigo, Victoria, Australia</Typography>
            <Typography variant="body1">
              Email: <a href="mailto:hello@corianpark.au">hello@corianpark.au</a>
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mt: 4, color: "text.secondary" }}>
            See also our{" "}
            <Box component={RouterLink} to="/privacy" sx={{ color: "primary.main", textDecoration: "none" }}>
              Privacy Policy
            </Box>
            .
          </Typography>
        </Box>
      </Box>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// 404
// ────────────────────────────────────────────────────────────────
export function NotFound() {
  return (
    <>
      <SEO title="Not found" description="That page doesn't exist." canonical="https://corianpark.au/" />
      <Typography variant="h2" sx={{ fontSize: 40, mb: 2 }}>404</Typography>
      <Typography variant="body1" color="text.secondary">That page doesn't exist.</Typography>
    </>
  );
}
